import dayjs from 'dayjs'
import { computed, onUnmounted, ref, toValue } from 'vue'

import { getI18n } from '@/plugins/i18n'
import { usePortalStore } from '@/stores/portal.store'

const TICK_MS = 30000
const FEW_SECONDS = 45
const CALENDAR_DAYS = 7
const TIME_UNITS = Object.freeze([
  { unit: 'minute', seconds: 60 },
  { unit: 'hour', seconds: 3600 },
])
const DATE_UNITS = Object.freeze([
  { unit: 'year', days: 365 },
  { unit: 'month', days: 30 },
  { unit: 'week', days: 7 },
])

const now = ref(Date.now())
let subscribers = 0
let timer = null

const subscribe = () => {
  subscribers++
  timer ??= setInterval(() => { now.value = Date.now() }, TICK_MS)
}

const unsubscribe = () => {
  subscribers--
  if (subscribers) return
  clearInterval(timer)
  timer = null
}

const relativeLabel = (date, language, fewSecondsLabel) => {
  const formatter = new Intl.RelativeTimeFormat(language, { numeric: 'auto' })
  const seconds = Math.round((date.valueOf() - now.value) / 1000)
  if (Math.abs(seconds) < FEW_SECONDS) return fewSecondsLabel
  const days = date.startOf('day').diff(dayjs(now.value).startOf('day'), 'day')
  if (!days) {
    const { unit, seconds: size } = TIME_UNITS.findLast(item => Math.abs(seconds) >= item.seconds) ?? TIME_UNITS[0]
    return formatter.format(Math.round(seconds / size), unit)
  }
  if (Math.abs(days) < CALENDAR_DAYS) return formatter.format(days, 'day')
  const { unit, days: size } = DATE_UNITS.find(item => Math.abs(days) >= item.days)
  return formatter.format(Math.round(days / size), unit)
}

export const useRelativeTime = (value) => {
  const portalStore = usePortalStore()
  const i18n = getI18n()
  subscribe()
  onUnmounted(unsubscribe)

  const date = computed(() => dayjs(toValue(value)))
  const label = computed(() => relativeLabel(date.value, portalStore.selectedLanguage, i18n.t('common.fewSecondsAgo')))
  const fullLabel = computed(() => new Intl.DateTimeFormat(portalStore.selectedLanguage, { dateStyle: 'full', timeStyle: 'short' }).format(date.value.toDate()))

  return { label, fullLabel }
}
